import { useEffect, useState } from 'react';
import { X, User, Mail, Calendar, Trash2 } from 'lucide-react';

interface EnrollmentWithUser {
  id: string;
  user_id: string;
  class_id: string;
  status: 'ACTIVE' | 'CANCELLED';
  created_at: string;
  updated_at: string;
  user?: {
    id: string;
    email: string;
    name?: string;
  };
}

interface ClassEnrollmentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  classId: string;
  classTitle: string;
}

export default function ClassEnrollmentsModal({
  isOpen,
  onClose,
  classId,
  classTitle,
}: ClassEnrollmentsModalProps) {
  const [enrollments, setEnrollments] = useState<EnrollmentWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && classId) {
      fetchEnrollments();
    }
  }, [isOpen, classId]);

  const fetchEnrollments = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/provider/classes/${classId}/enrollments`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch enrollments');
      }

      const data = await response.json();
      setEnrollments(data.enrollments || []);
    } catch (error) {
      console.error('Failed to fetch class enrollments:', error);
      alert('수강 신청 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEnrollment = async (enrollmentId: string, userEmail: string) => {
    const confirmed = confirm(
      `${userEmail} 사용자의 수강 신청을 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`
    );
    if (!confirmed) return;

    setDeletingId(enrollmentId);

    try {
      const response = await fetch(
        `/api/v1/provider/classes/${classId}/enrollments/${enrollmentId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '수강 신청 삭제에 실패했습니다.');
      }

      alert('수강 신청이 삭제되었습니다.');
      fetchEnrollments(); // 목록 새로고침
    } catch (error: any) {
      console.error('Failed to delete enrollment:', error);
      alert(error.message || '수강 신청 삭제에 실패했습니다.');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">수강 신청 목록</h2>
            <p className="text-sm text-gray-600 mt-1">{classTitle}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* 내용 */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : enrollments.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                수강 신청자가 없습니다
              </h3>
              <p className="text-gray-600">아직 이 수업에 신청한 사용자가 없습니다.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  총 <span className="font-semibold text-primary">{enrollments.length}명</span>이
                  신청했습니다.
                </p>
              </div>

              {enrollments.map((enrollment) => {
                const user = enrollment.user;
                if (!user) return null;

                return (
                  <div
                    key={enrollment.id}
                    className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <User className="w-5 h-5 text-gray-400" />
                          <span className="font-medium text-gray-900">
                            {user.name || '이름 없음'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                          <Mail className="w-4 h-4" />
                          <span>{user.email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Calendar className="w-4 h-4" />
                          <span>신청일: {formatDate(enrollment.created_at)}</span>
                        </div>
                      </div>

                      {/* 삭제 버튼 */}
                      <button
                        onClick={() => handleDeleteEnrollment(enrollment.id, user.email)}
                        disabled={deletingId === enrollment.id}
                        className={`ml-4 p-2 rounded-lg transition-colors ${
                          deletingId === enrollment.id
                            ? 'bg-gray-200 text-gray-400 cursor-wait'
                            : 'bg-red-50 text-red-600 hover:bg-red-100'
                        }`}
                        title="수강 신청 삭제"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div className="p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}
